/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import * as _ from 'lodash';
import template from './team-group-edit-page.component.html';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {TeamApiService} from '../team/team-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {TeamGroupApiService, TeamGroupModel} from './team-group-api.service';
import {FormsModule} from '@angular/forms';
import {APP_SHARED} from '../../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule],
  selector: 'adn-team-group-edit-page',
  template
})
export class TeamGroupEditPageComponent extends SignalAdminResourceEditPageBase<TeamGroupModel> {
  teamTypes: string[] = [];
  private readonly allTeamsState = signal<any[] | null>(null);
  get allTeams(): any[] | null { return this.allTeamsState(); }
  set allTeams(value: any[] | null) { this.allTeamsState.set(value); }
  canUpdateTeamGroup = false;
  teamsWarning = false;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(TeamGroupApiService), 'teamGroup');
    this.canUpdateTeamGroup = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');
    this.teamTypes = getAvailableTeamTypes(this.LocalNetworkProfile);

    this.teamApi.listOptions({minimal: true}).subscribe((page) => {
      this.allTeams = page.results;
    });
  }

  get filteredTeams(): any[] {
    if (!this.editingModel?.type || !this.allTeams) {
      return [];
    }
    return _.filter(this.allTeams, ['type', this.editingModel.type]);
  }

  protected override initialiseModel(model: TeamGroupModel): TeamGroupModel {
    const initialised = super.initialiseModel(model);
    initialised.teams = this.normalizeTeamIds(initialised.teams);
    return initialised;
  }

  protected override afterSave(model: TeamGroupModel): void {
    model.teams = this.normalizeTeamIds(model.teams);
    model.type = _.get(model, 'type') || this.editingModel.type;
  }

  protected override prepareForSubmit(): void {
    if (this.isNew) {
      this.localNetworkInfo.increment('teamGroupsCount');
    }
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.filteredTeams, 'id');
    this.markExternalFormDirty();
  }

  onTypeChange(): void {
    this.deselectTeams();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);

    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }

    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  private normalizeTeamIds(teams: any): string[] {
    return _.map(teams || [], (team: any) => _.isString(team) ? team : team.id);
  }
}

function getAvailableTeamTypes(LocalNetworkProfile: any): string[] {
  const isSuiPlatform = LocalNetworkProfile.isSuiPlatform();
  const isAdvertisingPlatform = LocalNetworkProfile.isAdvertisingPlatform();
  const isMarketplacePlatform = LocalNetworkProfile.isMarketplacePlatform();

  let teamTypes: string[] = [];
  if (isSuiPlatform) {
    teamTypes = ['STANDARD', 'SUI', 'AX_ADVERTISER', 'AX_PUBLISHER'];
  } else if (isAdvertisingPlatform) {
    teamTypes = ['STANDARD'];
    if (isMarketplacePlatform) {
      teamTypes.push('AX_ADVERTISER');
      teamTypes.push('AX_PUBLISHER');
      teamTypes.push('CREATIVE_AGENCY');
    }
  }

  if (LocalNetworkProfile.get('politicalApp')) {
    teamTypes = _.uniq(teamTypes.concat(['CONTROLLER', 'SPONSOR']));
  }

  return teamTypes;
}
